import { ViewTypes } from 'app/app.types';
import { BookingMethods, IBooking, ICustomer, ITableItem } from 'shared-types/SharedTypes';
import { blockoutTypeEnums } from 'app/common/BlockoutsByService/types';
import { MainViewType } from 'app/services/mainViewVisibility/types';

/**
 * Purpose of these "Actions" is to mimic a more redux-style of event management, which
 * should make transitioning to redux a little easier when the time comes.
 */

const NS = 'ViewActionsService';

export const VIEW_CHANGED = 'VIEW_CHANGED';
export const TIMELINE_SCROLL_CHANGED = 'TIMELINE_SCROLL_CHANGED';
// Note: there are still many instances pf 'edit-booking' thorughout the app, that aren't using EDIT_BOOKING yet
export const EDIT_BOOKING = 'edit-booking';
export const EDIT_BOOKING_OPENED = 'EDIT_BOOKING_OPENED';
export const EDIT_BOOKING_CLOSED = 'EDIT_BOOKING_CLOSED';
export const EDIT_CUSTOMER_OPENED = 'EDIT_CUSTOMER_OPENED';
export const EDIT_CUSTOMER_CLOSED = 'EDIT_CUSTOMER_CLOSED';
export const BLOCKOUT_BY_TABLE = 'BLOCKOUT_BY_TABLE';
export const BLOCKOUT_FINISHED = 'BLOCKOUT_FINISHED';
export const EDIT_BLOCKOUT = 'EDIT_BLOCKOUT';

export const SERVICE_CHANGED = 'SERVICE_CHANGED';
export const SECTION_CHANGED = 'SECTION_CHANGED';
export const CLOSE_DRAG_BOOKING_CONFIRMATION = 'CLOSE_DRAG_BOOKING_CONFIRMATION';
// Floorplan MVP
export const CONFLICTED_TABLE_CLICK = 'CONFLICTED_TABLE_CLICK';
export const BOOKING_VIEW_CHANGED = 'BOOKING_VIEW_CHANGED';

export interface IEditBookingData {
  booking: IBooking;
}

class ViewActionsService {
  private $rootScope: any;
  private readyCB: () => void;

  setup($rootScope: any) {
    this.$rootScope = $rootScope;
    if (this.readyCB) {
      this.readyCB();
    }
  }

  registerForReadyCB(cb: () => void) {
    if (this.$rootScope) {
      cb();
    } else {
      this.readyCB = cb;
    }
  }

  /**
   * viewId {number} - value from `appConstants.ViewTypes`
   * Subscribe from a controller like this:
   * this.listeners.push(
   $rootScope.$on(VIEW_CHANGED, (evt, data) => {
              if (data.payload === appConstants.ViewTypes.floorplan) {
                // view specific stuff here
              }
            })
   );
   */
  onViewChanged(viewId: ViewTypes) {
    return this.$rootScope.$broadcast(VIEW_CHANGED, { payload: viewId });
  }

  onTimelineScrollChanged() {
    return this.$rootScope.$broadcast(TIMELINE_SCROLL_CHANGED);
  }

  onEditBooking(payload: IEditBookingData) {
    return this.$rootScope.$broadcast(EDIT_BOOKING, null, payload.booking);
  }
  onEditBlockout() {
    return this.$rootScope.$broadcast(EDIT_BLOCKOUT, null);
  }
  onEditBookingOpened(payload: IEditBookingData) {
    return this.$rootScope.$broadcast(EDIT_BOOKING_OPENED, { payload });
  }

  // isHardClose will be false when another dialog (like edit customer) will be opened immediately afterwards
  onEditBookingClosed(isHardClose: boolean) {
    return this.$rootScope.$broadcast(EDIT_BOOKING_CLOSED, { payload: { isHardClose } });
  }

  onEditCustomerOpened() {
    return this.$rootScope.$broadcast(EDIT_CUSTOMER_OPENED);
  }

  // isHardClose will be false when another dialog (like edit booking) will be opened immediately afterwards
  onEditCustomerClosed(isHardClose: boolean) {
    return this.$rootScope.$broadcast(EDIT_CUSTOMER_CLOSED, { payload: { isHardClose } });
  }
  onBlockoutByTable(type: blockoutTypeEnums) {
    return this.$rootScope.$broadcast(BLOCKOUT_BY_TABLE, {payload: { type }});
  }
  onBlockoutFinished(type: blockoutTypeEnums) {
    return this.$rootScope.$broadcast(BLOCKOUT_FINISHED, {payload: { type }});
  }

  onServiceChanged(serviceIds?: string[]) {
    return this.$rootScope.$broadcast(SERVICE_CHANGED, { payload: serviceIds });
  }
  onSectionChanged(sectionIds?: string[]) {
    return this.$rootScope.$broadcast(SECTION_CHANGED, { payload: sectionIds });
  }

  onConflictedTableClick(tableId: string) {
    return this.$rootScope.$broadcast(CONFLICTED_TABLE_CLICK, { payload: tableId });
  }

  onBookingViewChanged(viewType: MainViewType) {
    return this.$rootScope.$broadcast(BOOKING_VIEW_CHANGED, { payload: viewType });
  }

  onCloseDragBookingConfirmation() {
    return this.$rootScope.$broadcast(CLOSE_DRAG_BOOKING_CONFIRMATION);
  }
  handleCloseDragBookingConfirmation(cb: () => void) {
    return this.$rootScope.$on(CLOSE_DRAG_BOOKING_CONFIRMATION, () => cb());
  }
  handleEditBlockout(cb: (evt: any) => void) {
    return this.$rootScope.$on(
      EDIT_BLOCKOUT,
      (evt: any) => cb(evt)
    );
  }
  handleEditBookingOpened(cb: (payload: IEditBookingData) => void) {
    return this.$rootScope.$on(
      EDIT_BOOKING_OPENED,
      (evt: any, data: { payload: IEditBookingData }) => cb(data.payload)
    );
  }

  handleEditBookingClosed(cb: () => void) {
    return this.$rootScope.$on(EDIT_BOOKING_CLOSED, () => cb());
  }
  handleBlockoutByTable(cb: (type: blockoutTypeEnums) => void) {
    return this.$rootScope.$on(BLOCKOUT_BY_TABLE, (e: any, data: any) => {
      cb(data.payload?.type)
    });
  }
  handleBlockoutFinished(cb: (type: blockoutTypeEnums) => void) {
    return this.$rootScope.$on(BLOCKOUT_FINISHED, (e: any, data: any) => cb(data.payload?.type));
  }

  handleServiceChanged(cb: () => void) {
    return this.$rootScope.$on(SERVICE_CHANGED, () => cb());
  }
  handleSectionChanged(cb: (payload: string[]) => void) {
    return this.$rootScope.$on(SECTION_CHANGED, (evt: any, data: { payload: string[] }) =>
      cb(data.payload)
    );
  }

  handleConflictedTableClick(cb: (payload: string) => void) {
    return this.$rootScope.$on(CONFLICTED_TABLE_CLICK, (evt: any, data: { payload: string }) =>
      cb(data.payload)
    );
  }

  handleBookingViewChanged(cb: (payload: MainViewType) => void) {
    return this.$rootScope.$on(BOOKING_VIEW_CHANGED, (evt: any, data: { payload: MainViewType }) =>
      cb(data.payload)
    );
  }
}

// singleton
const instance = new ViewActionsService();
export default instance;
